import { useCallback, useEffect, useState } from "react";
import { Link } from "../app/navigation.js";
import {
  ChevronRight, Copy, LoaderCircle, Lock, MoreHorizontal, Play, Share2, Trophy, X,
} from "lucide-react";
import { api, type MatchMode, type ReplayCursor, type ReplayRow } from "../net/api.js";
import { Button, EmptyState, Spinner } from "../ui/primitives.js";
import { useLocale } from "../i18n.js";
import "./replays.css";

type ReplayFilter = "all" | "wins" | "losses" | MatchMode;

const FILTERS = ["all", "ranked", "gold", "wins", "losses"] as const satisfies readonly ReplayFilter[];
const FILTER_LABELS: Record<ReplayFilter, "all" | "rankedFilter" | "goldFilter" | "wins" | "losses"> = {
  all: "all",
  ranked: "rankedFilter",
  gold: "goldFilter",
  wins: "wins",
  losses: "losses",
};

/** Finished matches, with playback and sharing treated as first-class actions. */
export function Replays() {
  const { t, number, date } = useLocale();
  const [rows, setRows] = useState<ReplayRow[] | null>(null);
  const [cursor, setCursor] = useState<ReplayCursor | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState<ReplayFilter>("all");

  const load = useCallback(async (next?: ReplayCursor) => {
    try {
      const page = await api.replays(next);
      setRows((current) => next === undefined ? page.replays : [...(current ?? []), ...page.replays]);
      setCursor(page.cursor);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function copyLink(url: string, roomId: string) {
    try {
      await navigator.clipboard.writeText(new URL(url, window.location.origin).toString());
      setCopied(roomId);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setNotice(new URL(url, window.location.origin).toString());
    }
  }

  async function toggleShare(row: ReplayRow) {
    if (busy) return;
    setNotice(null);
    setBusy(row.roomId);
    try {
      if (row.shared) {
        await api.unshareReplay(row.roomId);
        setRows((current) => current?.map((entry) => entry.roomId === row.roomId
          ? { ...entry, shared: false, shareSlug: null }
          : entry) ?? null);
      } else {
        const { slug, url } = await api.shareReplay(row.roomId);
        setRows((current) => current?.map((entry) => entry.roomId === row.roomId
          ? { ...entry, shared: true, shareSlug: slug }
          : entry) ?? null);
        await copyLink(url, row.roomId);
      }
    } catch {
      setNotice(t("somethingWrong"));
    } finally {
      setBusy(null);
    }
  }

  const shown = (rows ?? []).filter((row) =>
    filter === "all"
      ? true
      : filter === "wins"
        ? row.won
        : filter === "losses"
          ? !row.won
          : row.mode === filter,
  );

  const body = failed && rows === null ? (
    <EmptyState
      title={t("couldNotLoad")}
      hint={t("tryAgain")}
      action={<Button onClick={() => void load()}>{t("retry")}</Button>}
    />
  ) : rows === null ? (
    <Spinner label={t("loading")} />
  ) : rows.length === 0 ? (
    <EmptyState title={t("replayEmpty")} hint={t("replayEmptyHint")} />
  ) : shown.length === 0 ? (
    <EmptyState title={t("noMatchesForFilter")} hint={t("replayFilterHint")} />
  ) : (
    <ol className="replay-list">
      {shown.map((row) => (
        <li key={row.roomId} className={`replay-row${row.won ? " replay-won" : ""}`}>
          <Link className="replay-open" to={`/record/replay/${row.roomId}`}>
            <span className="replay-result-mark" aria-hidden="true">
              {row.won ? <Trophy /> : <X />}
            </span>
            <span className="replay-meta">
              <strong>{date(row.endedAt)}</strong>
              <span className="replay-sub">
                <span className={`replay-mode replay-mode-${row.mode}`}>
                  {row.mode === "ranked" ? t("ranked") : t("goldMode")}
                </span>
                <span>{row.won ? t("win") : t("lose")}</span>
                <span className="tabular">{t("handNumber", { count: number(row.hands) })}</span>
              </span>
            </span>
            <span className="replay-watch" aria-hidden="true"><Play fill="currentColor" /><ChevronRight /></span>
          </Link>

          <div className="replay-actions">
            {row.shared && row.shareSlug !== null && (
              <button
                type="button"
                className={`replay-action-button${copied === row.roomId ? " is-success" : ""}`}
                aria-label={copied === row.roomId ? t("shareLinkCopied") : t("shared")}
                title={copied === row.roomId ? t("shareLinkCopied") : t("shared")}
                onClick={() => void copyLink(`/r/${encodeURIComponent(row.shareSlug as string)}`, row.roomId)}
              >
                <Copy aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className={`replay-action-button replay-share-button${row.shared ? " is-shared" : ""}`}
              disabled={busy === row.roomId}
              aria-label={row.shared ? t("makePrivate") : t("makePublic")}
              title={row.shared ? t("makePrivate") : t("makePublic")}
              onClick={() => void toggleShare(row)}
            >
              {busy === row.roomId
                ? <LoaderCircle className="spin" aria-hidden="true" />
                : row.shared
                  ? <Lock aria-hidden="true" />
                  : <Share2 aria-hidden="true" />}
            </button>
          </div>
        </li>
      ))}
    </ol>
  );

  return (
    <section className="replay-library">
      <header className="replay-library-head">
        <div>
          <span>{t("replays")}</span>
          <h2>{t("myReplays")}</h2>
        </div>
        {rows !== null && <strong className="tabular">{number(rows.length)}</strong>}
      </header>

      <div className="replay-filters" role="group" aria-label={t("myReplays")}>
        {FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={filter === option}
            className={filter === option ? "is-active" : ""}
            onClick={() => setFilter(option)}
          >
            {t(FILTER_LABELS[option])}
          </button>
        ))}
      </div>

      {notice && <p role="status" className="section-hint" style={{ overflowWrap: "anywhere" }}>{notice}</p>}
      <div className="replay-library-body">{body}</div>

      {failed && rows !== null && <p className="replay-load-error" role="alert">{t("tryAgain")}</p>}
      {cursor !== null && rows !== null && rows.length > 0 && (
        <Button className="replay-more" variant="ghost" onClick={() => void load(cursor)}>
          <MoreHorizontal aria-hidden="true" />{t("more")}
        </Button>
      )}
    </section>
  );
}
